import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Table,
  Checkbox,
  Button,
  Space,
  Card,
  Row,
  Col,
  message,
  Divider,
} from "antd";
import {
  FilePdfOutlined,
  DownloadOutlined,
  PrinterOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import html2pdf from "html2pdf.js";
import Docxtemplater from "docxtemplater";
import PizZip from "pizzip";
import { saveAs } from "file-saver";
import api from "../../services/api";
import vendorSeal from "../../assets/seal_nms.jpeg";
import vendorSign from "../../assets/vendor_sign.png";

const { Option } = Select;
const { TextArea } = Input;

const DEBUG_PROFORMA = false;

const dbg = (...args) => {
  if (!DEBUG_PROFORMA) return;
  console.log("[PROFORMA DEBUG]", ...args);
};

// ===============================
// Helper Functions (EXACT same as MSEBdocsPage)
// ===============================

// Parse capacity from inverter model
const parseInverterCapacity = (modelNo) => {
  if (!modelNo) return "";
  const patterns = [
    /(\d+(\.\d+)?)\s*KW/i,
    /(\d+(\.\d+)?)\s*K\s*W/i,
    /^(\d+(\.\d+)?)\s*/,
    /[^\d]*(\d+(\.\d+)?)[^\d]*$/,
  ];
  for (const pattern of patterns) {
    const match = modelNo.match(pattern);
    if (match) return match[1];
  }
  return "";
};

// Format project type from enum to display value
const formatProjectType = (projectType) => {
  if (!projectType) return "Capex";
  
  const projectTypeMap = {
    Residential: "Capex",
    PMSuryaghar: "PMSuryaghar",
    SocietyPMSuryaghar: "Society PMSuryaghar",
    IndividualPMSuryaghar: "Individual PMSuryaghar",
    Commercial: "Commercial",
    Industrial: "Industrial"
  };
  
  return projectTypeMap[projectType] || projectType;
};

// Connection type display
const getConnectionTypeDisplay = (connectionType) => {
  switch (connectionType) {
    case "SinglePhase":
      return "230 V Single Phase";
    case "ThreePhase":
      return "415 V Three Phase";
    case "Mixed":
      return "Mixed Phase";
    default:
      return "230 V Single Phase";
  }
};

// Category from customer data (copied from MSEBdocsPage)
const getCategoryFromCustomer = (customer) => {
  if (customer.locationType === "Rural") return "Rural";
  if (customer.locationType === "Urban") return "Urban";
  if (customer.houseOwnership === "Single") return "Individual";
  if (customer.houseOwnership === "Joint") return "Joint";
  return "Private";
};

/**
 * ✅ EXACT SAME fetchCustomerDetails as MSEBdocsPage
 */
const fetchCustomerDetails = async (customerId) => {
  try {
    dbg(`Fetching customer details for ID: ${customerId}`);
    const json = await api.get(`/customers/${customerId}`);
    
    if (!json.success || !json.data) {
      dbg("fetchCustomerDetails unexpected json:", json);
      return null;
    }

    const c = json.data;

    // ✅ EXACT normalization as MSEBdocsPage
    const ordersRaw = c.orders || c.order || [];
    const normalizedOrders = (Array.isArray(ordersRaw) ? ordersRaw : [ordersRaw])
      .filter(Boolean)
      .map((o) => ({
        ...o,
        items: o.items || o.orderitem || [],
      }));

    const normalizedAssets = c.assets || c.serializedasset || [];

    const normalizedCustomer = {
      ...c,
      orders: normalizedOrders,
      assets: normalizedAssets,
    };

    dbg("customer normalized:", {
      id: normalizedCustomer.id,
      name: normalizedCustomer.customerName,
      ordersCount: normalizedCustomer.orders?.length,
      firstOrderItemsCount: normalizedCustomer.orders?.[0]?.items?.length,
      assetsCount: normalizedCustomer.assets?.length,
    });

    return normalizedCustomer;
  } catch (error) {
    dbg("fetchCustomerDetails network error:", error);
    return null;
  }
};

/**
 * ✅ EXACT SAME extractAvailableOrderDetails as MSEBdocsPage
 */
const extractAvailableOrderDetails = (order, customerAssets = []) => {
  dbg("extractAvailableOrderDetails START", { 
    orderId: order?.id, 
    assetsCount: customerAssets?.length 
  });

  const details = {
    moduleMake: "",
    moduleModel: "",
    moduleWattage: "",
    numberOfModules: "",
    totalCapacity: "",
    warrantyDetails: "",

    inverterModel: "",
    inverterCapacity: "",
    inverterRating: "",

    mppt: "1 MPPT",
    hpd: "Yes",
    manufacturingYear: "",
    chargeControllerType: "MPPT",

    panelWarrantyYears: order?.panelWarrantyYears?.toString() || "",
    inverterWarrantyYears: order?.inverterWarrantyYears?.toString() || "",
    plantWarrantyYears: order?.plantWarrantyYears?.toString() || "",
  };

  const orderAssets = (customerAssets || []).filter(
    (asset) => asset.orderId === order?.id || asset.customerId === order?.customerId
  );

  const orderItems = order?.items || [];
  
  dbg("extractAvailableOrderDetails:", {
    orderId: order?.id,
    itemsCount: orderItems.length,
    assetsCount: orderAssets.length,
  });

  // ============ FROM ORDER ITEMS ============
  if (orderItems && orderItems.length > 0) {
    const inverterItems = orderItems.filter(
      (it) =>
        it.item?.itemType === "Inverter" || 
        it.item?.itemName?.toLowerCase().includes("inverter")
    );

    const solarItems = orderItems.filter(
      (it) =>
        it.item?.itemType === "SolarPanel" ||
        it.item?.itemName?.toLowerCase().includes("solar") ||
        it.item?.itemName?.toLowerCase().includes("panel")
    );

    // Solar Panel Details from Items
    if (solarItems.length > 0) {
      const solarItem = solarItems[0];
      const itemData = solarItem.item || {};

      // Module Make
      if (itemData.itemName && !details.moduleMake) {
        const makeFromName = itemData.itemName.split(" ")[0];
        if (makeFromName && makeFromName.length > 2) details.moduleMake = makeFromName;
      }

      // Module Wattage
      if (!details.moduleWattage) {
        let wattage = 540; // Default
        
        if (itemData.modelNo) {
          const wattageMatch = itemData.modelNo.match(/(\d+)\s*WP?/i);
          if (wattageMatch) wattage = parseInt(wattageMatch[1], 10);
        }

        if (itemData.itemName && wattage === 540) {
          const wattageMatch = itemData.itemName.match(/(\d+)\s*W/);
          if (wattageMatch) wattage = parseInt(wattageMatch[1], 10);
        }

        details.moduleWattage = String(wattage);
        dbg("Found module wattage from items:", details.moduleWattage);
      }

      // ✅ EXACT same as MSEBdocsPage - get number of modules from quantity
      if (solarItem.quantity) {
        details.numberOfModules = String(solarItem.quantity);
        dbg("✓ Found module quantity from ORDER ITEMS:", details.numberOfModules);
      }
    }

    // Inverter Details from Items
    if (inverterItems.length > 0) {
      const inverterItem = inverterItems[0];
      const itemData = inverterItem.item || {};

      let inverterModelStr = "";

      if (itemData.itemName) {
        inverterModelStr = itemData.itemName;
        dbg("Inverter name from items:", itemData.itemName);
      }

      if (itemData.modelNo) {
        inverterModelStr = inverterModelStr
          ? `${inverterModelStr} - ${itemData.modelNo}`
          : itemData.modelNo;
      }

      details.inverterModel = inverterModelStr;
      dbg("Inverter model from items:", details.inverterModel);
    }
  }

  // ============ FROM SERIALIZED ASSETS (HIGHER PRIORITY) ============
  
  // Inverter Assets
  const inverterAssets = orderAssets.filter((asset) => {
    if (asset.item && asset.item.itemType === "Inverter") return true;
    if (asset.brand && asset.brand.toLowerCase().includes("inverter")) return true;
    if (asset.item?.itemName?.toLowerCase().includes("inverter")) return true;
    return false;
  });

  dbg("Found inverter assets:", inverterAssets.length);

  if (inverterAssets.length > 0) {
    const makeModelList = inverterAssets
      .map((a) => {
        const name = a?.item?.itemName?.trim() || a?.brand?.trim() || "Inverter";
        const number = a?.item?.modelNo?.trim() || 
                      a?.batchId?.trim() || 
                      a?.invoiceNumber?.trim() || 
                      a?.serialNumber?.trim() || "";
        return [name, number].filter(Boolean).join(" - ");
      })
      .filter(Boolean);

    const uniqueMakeModels = [...new Set(makeModelList)];
    if (uniqueMakeModels.length > 0) {
      details.inverterModel = uniqueMakeModels.join(", ");
      dbg("Inverter model from assets:", details.inverterModel);
    }

    const inverterAsset = inverterAssets[0];
    
    if (inverterAsset?.purchaseDate && !details.manufacturingYear) {
      details.manufacturingYear = new Date(inverterAsset.purchaseDate).getFullYear().toString();
      dbg("Manufacturing year from asset:", details.manufacturingYear);
    }

    // Use the capacity field directly from serialized asset
    dbg("Inverter asset capacity raw:", inverterAsset?.capacity);
    if (inverterAsset?.capacity != null && inverterAsset.capacity !== 0) {
      details.inverterCapacity = String(inverterAsset.capacity);
      dbg("Inverter capacity from asset.capacity:", details.inverterCapacity);
    }
    // Fallback: parse KW from item name only
    if (!details.inverterCapacity) {
      const kwMatch = (inverterAsset?.item?.itemName || "").match(/(\d+(\.\d+)?)\s*KW/i);
      if (kwMatch) {
        details.inverterCapacity = kwMatch[1];
        dbg("Inverter capacity from itemName KW match:", kwMatch[1]);
      }
    }
  }

  // Solar Panel Assets
  // ============ FROM SERIALIZED ASSETS (HIGHER PRIORITY) ============
// Solar Panel Assets
const solarPanelAssets = orderAssets.filter((asset) => {
  if (asset.item && asset.item.itemType === "SolarPanel") return true;
  if (asset.item?.itemName?.toLowerCase().includes("panel")) return true;
  if (asset.item?.itemName?.toLowerCase().includes("solar")) return true;
  if (asset.brand?.toLowerCase().includes("solar")) return true;
  if (asset.brand?.toLowerCase().includes("premier")) return true;
  return false;
});

dbg("Found solar panel assets:", solarPanelAssets.length);

if (solarPanelAssets.length > 0) {
  const solarPanelAsset = solarPanelAssets.find((asset) => asset.brand) || solarPanelAssets[0];

  // Module Make from brand
  if (solarPanelAsset?.brand && solarPanelAsset.brand.length > 2) {
    details.moduleMake = solarPanelAsset.brand;
    dbg("Module make from asset:", details.moduleMake);
  }

  // Module Wattage from asset (prefer SerializedAsset.capacity)
  if (!details.moduleWattage) {
    let wattage = null;

    // Prefer stored capacity on SerializedAsset as module wattage
    if (solarPanelAsset?.capacity) {
      const cap = parseInt(String(solarPanelAsset.capacity), 10);
      if (Number.isFinite(cap) && cap > 0) wattage = String(cap);
    }
    
    if (!wattage && solarPanelAsset?.item?.modelNo) {
      const match = solarPanelAsset.item.modelNo.match(/(\d+)\s*WP?/i);
      if (match) wattage = match[1];
    }
    
    if (!wattage && solarPanelAsset?.item?.itemName) {
      const match = solarPanelAsset.item.itemName.match(/(\d+)\s*W/i);
      if (match) wattage = match[1];
    }
    
    if (!wattage && solarPanelAsset?.model) {
      const match = solarPanelAsset.model.match(/(\d+)\s*WP?/i);
      if (match) wattage = match[1];
    }
    
    if (wattage) {
      details.moduleWattage = String(wattage);
      dbg("Module wattage from serialized asset:", details.moduleWattage);
    }
  }

  // Always use asset count for modules (same as WCR logic)
  details.numberOfModules = solarPanelAssets.length.toString();
  dbg("✓ Module count from SERIALIZED ASSETS:", details.numberOfModules);
}

// ============ CALCULATE TOTAL CAPACITY ============
if (details.moduleWattage && details.numberOfModules) {
  const wattage = parseInt(details.moduleWattage, 10) || 540;
  const modules = parseInt(details.numberOfModules, 10) || 1;
  details.totalCapacity = ((modules * wattage) / 1000).toFixed(3);
  dbg("Calculated total capacity:", details.totalCapacity, "from", modules, "modules @", wattage, "W");
}

  // Inverter Rating from connection type
  if (order?.connectionType && !details.inverterRating) {
    details.inverterRating = getConnectionTypeDisplay(order.connectionType);
    dbg("Inverter rating from connection type:", details.inverterRating);
  }

  // Warranty Details
  if (order?.panelWarrantyYears > 0 || order?.inverterWarrantyYears > 0 || order?.plantWarrantyYears > 0) {
    details.warrantyDetails =
      `Panel: ${order.panelWarrantyYears || 0} years, ` +
      `Inverter: ${order.inverterWarrantyYears || 0} years, ` +
      `Plant: ${order.plantWarrantyYears || 0} years`;
    dbg("Warranty details:", details.warrantyDetails);
  }

  dbg("extractAvailableOrderDetails COMPLETE", {
    numberOfModules: details.numberOfModules || "NOT FOUND",
    moduleWattage: details.moduleWattage,
    totalCapacity: details.totalCapacity || "NOT CALCULATED"
  });
  
  return details;
};

const ProformaGenerator = ({ visible, onCancel, orders, customers }) => {
  const [form] = Form.useForm();
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [proformaData, setProformaData] = useState(null);
  const [customerAssets, setCustomerAssets] = useState([]);
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [previewHTML, setPreviewHTML] = useState("");
  const [searchText, setSearchText] = useState("");
  const previewRef = useRef(null);
  const [signImageData, setSignImageData] = useState(null);
  const [sealImageData, setSealImageData] = useState(null);

  // Load signature and seal images from public folder (optional)
  const imageUrlToDataUrl = async (url) => {
    try {
      if (typeof url === "string" && url.startsWith("data:")) return url;
      const response = await fetch(url);
      if (!response.ok) return null;
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch {
      return null;
    }
  };

  useEffect(() => {
    const loadImages = async () => {
      const signData = await imageUrlToDataUrl(vendorSign);
      const sealData = await imageUrlToDataUrl(vendorSeal);
      if (signData) setSignImageData(signData);
      if (sealData) setSealImageData(sealData);
    };
    loadImages();
  }, []);

  // ===============================
  // FIXED VALUES
  // ===============================
  const FIXED = useMemo(
    () => ({
      reArrangementType: "Net metering Arrangement",
      reSource: "Solar",
      installedCapacityRooftopGround: "NA",
      installedCapacityGround: "NA",
    }),
    []
  );

  // ===============================
  // Fetch assets when customer changes
  // ===============================
  useEffect(() => {
    if (!selectedCustomer?.id) {
      setCustomerAssets([]);
      return;
    }

    const loadAssets = async () => {
      setLoadingAssets(true);
      try {
        const data = await api.get(`/inventory/assets?customerId=${selectedCustomer.id}`);
        const assets = data.success && Array.isArray(data.data) ? data.data : (Array.isArray(data) ? data : []);
        console.log("DEBUG ASSETS RAW:", JSON.stringify(assets.map(a => ({ id: a.id, itemType: a.item?.itemType, itemName: a.item?.itemName, capacity: a.capacity, serialNumber: a.serialNumber })), null, 2));
        setCustomerAssets(assets);
      } catch (error) {
        dbg("Error loading assets:", error);
        setCustomerAssets([]);
      }
      setLoadingAssets(false);
    };

    loadAssets();
  }, [selectedCustomer]);

  // ===============================
  // Auto-fill on customer + order select
  // ===============================
  useEffect(() => {
    if (!selectedCustomer || selectedOrders.length === 0) return;

    const order = selectedOrders[0];
    
    dbg("==================================");
    dbg("AUTO-FILL TRIGGERED");
    dbg("Selected Order:", {
      id: order.id,
      plantSizeKw: order.plantSizeKw,
      itemsCount: order.items?.length || 0,
    });
    
    // Get plant capacity
    const plantCapacity = order?.plantSizeKw || "3";
    const plantCapacityStr = plantCapacity ? String(plantCapacity) : "3";
    
    // Get installation date
    const installationDate = order?.installationCompletedDate || 
                            order?.orderDate || 
                            dayjs();
    
    // Get project type and format it
    const projectType = order?.projectType;
    const formattedProjectType = formatProjectType(projectType);
    
    // ✅ USE THE EXACT SAME EXTRACTION FUNCTION AS MSEBdocsPage
    const availableDetails = extractAvailableOrderDetails(order, customerAssets);
    
    dbg("Extracted Details - Module Count:", availableDetails.numberOfModules);
    dbg("Extracted Details - Module Wattage:", availableDetails.moduleWattage);
    
    // Calculate module capacity
    const moduleCapacity = plantCapacityStr ? `${plantCapacityStr}KW` : "3KW";

    const next = {
      // Customer fields
      customerName: selectedCustomer.customerName || "",
      consumerNo: order?.selectedConsumerNumber || selectedCustomer.consumerNo || "",
      mobileNumber: selectedCustomer.customerMobile || selectedCustomer.mobile || "",
      email: selectedCustomer.customerEmail || selectedCustomer.email || "",
      address: selectedCustomer.customerAddress || selectedCustomer.address || "",
      pincode: selectedCustomer.pincode || selectedCustomer.pinCode || "",
      district: selectedCustomer.district || "",
      state: selectedCustomer.state || "Maharashtra",

      // Fixed values
      reArrangementType: FIXED.reArrangementType,
      reSource: FIXED.reSource,
      projectModel: "Capex",
      installedCapacityRooftopGround: FIXED.installedCapacityRooftopGround,
      installedCapacityGround: FIXED.installedCapacityGround,

      // Capacities
      sanctionCapacity: plantCapacityStr,
      installedCapacity: plantCapacityStr,

      // Date
      installationDate: dayjs(installationDate),

      // ✅ EXTRACTED DETAILS
      moduleMake: availableDetails.moduleMake,
      moduleModel: availableDetails.moduleModel,
      moduleWattage: availableDetails.moduleWattage,
      numberOfModules: availableDetails.numberOfModules || "",
      totalCapacity: availableDetails.totalCapacity,
      warrantyDetails: availableDetails.warrantyDetails,

      inverterMaker: availableDetails.inverterModel,
      inverterCapacity: availableDetails.inverterCapacity,
      inverterRating: availableDetails.inverterRating,

      mppt: availableDetails.mppt,
      hpd: availableDetails.hpd,
      manufacturingYear: availableDetails.manufacturingYear,

      panelWarrantyYears: availableDetails.panelWarrantyYears,
      inverterWarrantyYears: availableDetails.inverterWarrantyYears,
      plantWarrantyYears: availableDetails.plantWarrantyYears,

      // Use actual module count from assets/order items
      noOfPVModules: availableDetails.numberOfModules || "",
      moduleCapacity: moduleCapacity,
    };

    dbg("Setting form values - noOfPVModules:", next.noOfPVModules);
    dbg("Setting form values - numberOfModules:", next.numberOfModules);
    dbg("==================================");
    
    form.setFieldsValue(next);
    
    // Generate preview
    setTimeout(() => {
      handleAutoPreview();
    }, 100);
    
  }, [selectedCustomer, selectedOrders, form, FIXED, customerAssets]);

  const handleAutoPreview = () => {
    const values = form.getFieldsValue();
    
    const data = {
      ...values,
      ...FIXED,
      selectedOrders: selectedOrders.map((o) => o.id),
      installationDate: values.installationDate
        ? values.installationDate.format("DD/MM/YYYY")
        : dayjs().format("DD/MM/YYYY"),
      generatedDate: dayjs().format("DD/MM/YYYY HH:mm"),
      customer: selectedCustomer,
    };

    setProformaData(data);
    const html = createProformaHTML(data);
    setPreviewHTML(html);
  };

  const handleOrderSelect = async (orderId, checked) => {
    if (checked) {
      const order = orders.find((o) => o.id === orderId);
      if (!order) return;

      // Only allow one order at a time
      setSelectedOrders([order]);

      // Reset previous customer data when switching orders
      setSelectedCustomer(null);
      setCustomerAssets([]);

      if (order.customerId) {
        setLoadingAssets(true);
        
        // First try to find customer in props
        let customer = customers.find((c) => c.id === order.customerId);
        
        // If not found, fetch FULL customer details with items and assets
        if (!customer) {
          dbg("Customer not found in props, fetching details for ID:", order.customerId);
          const detailedCustomer = await fetchCustomerDetails(order.customerId);
          if (detailedCustomer) {
            customer = detailedCustomer;
          }
        }
        
        setLoadingAssets(false);
        
        if (customer) {
          dbg("Auto selected customer:", customer.customerName);
          setSelectedCustomer(customer);
          
          // Get the specific order from the customer data which has items populated
          const customerOrder = customer.orders?.find(o => o.id === orderId);
          if (customerOrder) {
            dbg("Found customer order with items:", {
              orderId: customerOrder.id,
              itemsCount: customerOrder.items?.length,
              solarItem: customerOrder.items?.find(i => 
                i.item?.itemType === "SolarPanel" || 
                i.item?.itemName?.toLowerCase().includes("panel")
              )
            });
            
            // Update the selected order with the one that has items
            setSelectedOrders([customerOrder]);
          }
        }
      }
    } else {
      setSelectedOrders((prev) => prev.filter((o) => o.id !== orderId));
      
      if (selectedOrders.length === 1) {
        setSelectedCustomer(null);
        setCustomerAssets([]);
        setPreviewHTML("");
        form.resetFields();
      }
    }
  };


  // ============================================================
  // Generate Proforma as .docx using template
  // ============================================================
  const generateDocxProforma = async (data) => {
    try {
      const safe = (v) => (v === null || v === undefined ? "" : String(v));

      const response = await fetch("/templates/Proforma_template.docx");
      if (!response.ok) throw new Error("Failed to load Proforma template");
      const templateArrayBuffer = await response.arrayBuffer();

      const zip = new PizZip(templateArrayBuffer);
      const doc = new Docxtemplater(zip, {
        paragraphLoop: true,
        linebreaks: true,
      });

      // Build full address
      const addressLine = safe(data.address);
      const addressAlreadyHasDetails = addressLine.toLowerCase().includes("district") ||
        addressLine.toLowerCase().includes("state") || addressLine.toLowerCase().includes("pin");
      const districtLine = (!addressAlreadyHasDetails && data.district) ? `District: ${data.district}` : "";
      const stateLine = (!addressAlreadyHasDetails && data.state) ? `State: ${data.state}` : "";
      const pinLine = (!addressAlreadyHasDetails && data.pincode) ? `Pin Code: ${data.pincode}` : "";
      const fullAddress = [addressLine, districtLine, stateLine, pinLine].filter(Boolean).join(", ");

      const sanction = safe(data.sanctionCapacity) || "";
      const installedRooftop = safe(data.installedCapacity) || sanction;

      let noOfModules = safe(data.noOfPVModules) || safe(data.numberOfModules) || "";
      const moduleWattage = safe(data.moduleWattage) || "";

      let totalCapacity = safe(data.totalCapacity);
      if (!totalCapacity && noOfModules && moduleWattage) {
        const modules = parseInt(noOfModules, 10);
        const wattage = parseInt(moduleWattage, 10);
        if (!isNaN(modules) && !isNaN(wattage) && modules > 0 && wattage > 0) {
          totalCapacity = ((modules * wattage) / 1000).toFixed(2);
        }
      }

      const moduleCap = safe(data.moduleCapacity) || (totalCapacity ? `${totalCapacity}KW` : `${sanction}KW`);

      doc.render({
        customerName: safe(data.customerName),
        consumerNo: safe(data.consumerNo),
        mobileNumber: safe(data.mobileNumber),
        email: safe(data.email),
        address: fullAddress || safe(data.address),
        reArrangementType: safe(data.reArrangementType),
        reSource: safe(data.reSource),
        sanctionCapacity: sanction,
        projectModel: safe(data.projectModel),
        installedCapacityRooftop: installedRooftop,
        installationDate: safe(data.installationDate),
        inverterCapacity: safe(data.inverterCapacity),
        inverterMaker: safe(data.inverterMaker),
        noOfPVModules: noOfModules,
        moduleCapacity: moduleCap,
        totalCapacity: totalCapacity || sanction,
        fullAddress: fullAddress || safe(data.address),
        vendorName: safe(data.vendorName) || "Niyati Multi Services",
      });

      const output = doc.getZip().generate({
        type: "blob",
        mimeType: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });

      const fileName = `Commissioning_Report_${safe(data.consumerNo) || "Customer"}_${dayjs().format("YYYYMMDD")}.docx`;
      saveAs(output, fileName);

      message.success(`Proforma document generated: ${fileName}`);
      setGenerating(false);
    } catch (error) {
      console.error("DOCX generation error:", error);
      message.error("Failed to generate Proforma: " + error.message);
      setGenerating(false);
    }
  };

  const generateProforma = () => {
    form
      .validateFields()
      .then((values) => {
        setGenerating(true);

        const data = {
          ...values,
          ...FIXED,
          selectedOrders: selectedOrders.map((o) => o.id),
          installationDate: values.installationDate
            ? values.installationDate.format("DD/MM/YYYY")
            : dayjs().format("DD/MM/YYYY"),
          generatedDate: dayjs().format("DD/MM/YYYY HH:mm"),
          customer: selectedCustomer,
        };

        generateDocxProforma(data);
      })
      .catch((error) => {
        dbg("Validation failed:", error);
        message.error("Please fill all required fields");
      });
  };

  const generatePDF = (data) => {
    const proformaHTML = createProformaHTML(data);

    const element = document.createElement("div");
    element.innerHTML = proformaHTML;

    const opt = {
      margin: 10,
      filename: `Commissioning_Report_${data.consumerNo || "Customer"}_${dayjs().format("YYYYMMDD")}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
    };

    html2pdf()
      .from(element)
      .set(opt)
      .save()
      .then(() => {
        setGenerating(false);
        message.success("Commissioning Report generated successfully!");
      })
      .catch((err) => {
        dbg("html2pdf error:", err);
        setGenerating(false);
        message.error("Failed to generate PDF");
      });
  };

  // ===============================
  // PDF HTML
  // ===============================
  const createProformaHTML = (data) => {
    const safe = (v) => (v === null || v === undefined ? "" : String(v));

    const addressLine = safe(data.address);
    // Only append district/state/pin if address doesn't already contain them
    const addressAlreadyHasDetails = addressLine.toLowerCase().includes('district') ||
                                      addressLine.toLowerCase().includes('state') ||
                                      addressLine.toLowerCase().includes('pin');
    const districtLine = (!addressAlreadyHasDetails && data.district) ? `District: ${data.district}` : "";
    const stateLine = (!addressAlreadyHasDetails && data.state) ? `State: ${data.state}` : "";
    const pinLine = (!addressAlreadyHasDetails && data.pincode) ? `Pin Code: ${data.pincode}` : "";

    const addressFull = [addressLine, districtLine, stateLine, pinLine]
      .filter(Boolean)
      .join(addressLine ? ", " : "");

    const sanction = safe(data.sanctionCapacity) || "NA";
    const installedRooftop = safe(data.installedCapacity) || "NA";
    const inverterMake = safe(data.inverterMaker) || "NA";
    const inverterCapacity = safe(data.inverterCapacity) || "NA";
    
    // Get module count - try both field names, default to 6 to match proforma format
    let noOfModules = safe(data.noOfPVModules);
    if (!noOfModules || noOfModules === "0" || noOfModules === "") {
      noOfModules = safe(data.numberOfModules);
    }
    if (!noOfModules || noOfModules === "0" || noOfModules === "") {
      noOfModules = "";
    }
    
    const moduleWattage = safe(data.moduleWattage) || "NA";
    
    // Calculate total capacity if not provided
    let totalCapacity = safe(data.totalCapacity);
    if ((!totalCapacity || totalCapacity === "") && noOfModules && noOfModules !== "" && noOfModules !== "0") {
      const modules = parseInt(noOfModules, 10);
      const wattage = parseInt(moduleWattage, 10);
      if (!isNaN(modules) && !isNaN(wattage) && modules > 0 && wattage > 0) {
        totalCapacity = ((modules * wattage) / 1000).toFixed(2);
      }
    }
    
    const moduleCap = safe(data.moduleCapacity) || (totalCapacity ? `${totalCapacity}KW` : `${sanction}KW`);

    const displayModules = noOfModules;

    // Sign and seal images (from state)
    const signImg = signImageData ? `<img src="${signImageData}" alt="Signature" style="height:32px; max-width:90px; display:block; margin-bottom:4px;" />` : "";
    const sealImg = sealImageData ? `<img src="${sealImageData}" alt="Seal" style="height:32px; width:32px; display:inline-block; vertical-align:middle; margin-right:6px;" />` : "";

    const td = (content, extra = "") => `<td style="padding:6px 10px; border:1px solid #000; font-family:'Times New Roman',serif; font-size:12pt; color:#000; ${extra}">${content}</td>`;
    const tdBold = (content, extra = "") => td(`<strong>${content}</strong>`, extra);
    const tdCenter = (content) => td(content, "text-align:center;");

    return `
      <div style="font-family:'Times New Roman',serif; padding:14px 18px; color:#000; max-width:780px; margin:0 auto;">

        <!-- PAGE 1: Annexure-I -->
        <div>
          <div style="text-align:center; font-weight:700; font-size:12pt; font-family:'Times New Roman',serif; margin-bottom:2px;">Renewable Energy Generating System</div>
          <div style="text-align:center; font-weight:700; font-size:12pt; font-family:'Times New Roman',serif; margin-bottom:2px;">Annexure-I</div>
          <div style="text-align:center; font-weight:700; font-size:12pt; font-family:'Times New Roman',serif; margin-bottom:10px;">Commissioning Report for RE System</div>

          <table cellpadding="0" cellspacing="0" style="width:100%; border-collapse:collapse; font-family:'Times New Roman',serif; font-size:12pt; color:#000;">
            <colgroup>
              <col style="width:8%;" />
              <col style="width:33%;" />
              <col style="width:59%;" />
            </colgroup>
            <tr style="background:#f2f2f2;">
              ${td('<strong>Sr.No.</strong>', 'text-align:center; font-weight:700;')}
              ${td('<strong>Particulars</strong>', 'font-weight:700;')}
              ${td('<strong>As Commissioned</strong>', 'font-weight:700;')}
            </tr>
            <tr>${tdCenter('1')}${td('Name of The Consumer')}${tdBold(safe(data.customerName))}</tr>
            <tr>${tdCenter('2')}${td('Consumer number')}${tdBold(safe(data.consumerNo))}</tr>
            <tr>${tdCenter('3')}${td('Mobile Number')}${tdBold(safe(data.mobileNumber))}</tr>
            <tr>${tdCenter('4')}${td('E-mail')}${tdBold(safe(data.email))}</tr>
            <tr>${tdCenter('5')}${td('Address Of Installation')}${tdBold(safe(addressFull) || safe(data.address))}</tr>
            <tr>${tdCenter('6')}${td('RE Arrangement Type')}${td(safe(data.reArrangementType))}</tr>
            <tr>${tdCenter('7')}${td('RE Source')}${td(safe(data.reSource))}</tr>
            <tr>${tdCenter('8')}${td('Sanction Capacity (Kw)')}${td(sanction)}</tr>
            <tr>${tdCenter('9')}${td('Project Model')}${td(safe(data.projectModel))}</tr>
            <tr>${tdCenter('10')}${td('RE Installed Capacity (Rooftop)')}${td(installedRooftop)}</tr>
            <tr>${tdCenter('11')}${td('RE Installed Capacity (Rooftop+Ground)')}${td(safe(data.installedCapacityRooftopGround))}</tr>
            <tr>${tdCenter('12')}${td('RE Installed Capacity (Ground)')}${td(safe(data.installedCapacityGround))}</tr>
            <tr>${tdCenter('13')}${td('Installation Date')}${td(safe(data.installationDate))}</tr>
            <tr>${tdCenter('14')}${td('Solar PV Details')}${td('')}</tr>
            <tr>${td('')}${td('Inverter Capacity (Kw)')}${td(inverterCapacity)}</tr>
            <tr>${td('')}${td('Inverter Make')}${td(inverterMake)}</tr>
            <tr>${td('')}${td('No. of PV Modules')}${td(displayModules)}</tr>
            <tr>${td('')}${td('Module Capacity (kw)')}${td(moduleCap)}</tr>
          </table>
        </div>

        <!-- Proforma-A (same page) -->
        <div style="padding-top:6px;">
          <div style="text-align:center; font-weight:700; font-size:14pt; font-family:'Times New Roman',serif; margin-bottom:4px;">Proforma-A</div>
          <div style="text-align:center; font-weight:700; font-size:10pt; font-family:'Times New Roman',serif; margin-bottom:8px;">
            COMMISSIONING REPORT (PROVISIONAL) FOR GRID CONNECTED SOLAR PHOTOVOLTAIC POWER PLANT (with Net-metering facility)
          </div>

          <div style="font-size:10pt; font-family:'Times New Roman',serif; line-height:1.5; text-align:justify; color:#000;">
            Certified that a Grid Connected SPV Power Plant of <strong>${totalCapacity || sanction}</strong> KWp capacity has been installed at the
            <strong>${safe(addressFull) || safe(data.address)}</strong>
            which has been installed by <strong>M/S ${safe(data.vendorName) || 'Niyati Multi Services'}</strong> on <strong>${safe(data.installationDate)}</strong>.
            The system is as per BIS/MNRE specifications. The system has been checked for its performance and found in order for further commissioning.
          </div>

          <div style="margin-top:10px; display:flex; justify-content:space-between; align-items:flex-start; font-size:10pt; font-family:'Times New Roman',serif;">
            <div style="width:45%;">
              <div style="min-height:25px;"></div>
              <strong>Signature of the beneficiary</strong>
            </div>
            <div style="width:45%;">
              <div style="min-height:45px; display:flex; align-items:flex-end; gap:10px;">
                ${signImageData ? `<img src="${signImageData}" alt="Signature" style="height:42px; max-width:120px;" />` : ""}
                ${sealImageData ? `<img src="${sealImageData}" alt="Seal" style="height:52px; width:52px;" />` : ""}
              </div>
              <strong>Signature of the agency with name, seal and date</strong>
            </div>
          </div>

          <div style="page-break-before:always; padding-top:16px; font-size:10pt; font-family:'Times New Roman',serif; line-height:1.5; color:#000;">
            The above RTS installation has been inspected by me for Pre-Commissioning Testing of Roof Top Solar Connection on dt_______________ as per guidelines issued by the office of The Chief Engineer vide letter no _____________________ on dt. _____________________ and found in order for commissioning.
          </div>

          <div style="margin-top:10px; font-size:10pt; font-family:'Times New Roman',serif; color:#000;">
            <div style="margin-top:6px;"><strong>Signature of the MSEDCL Officer</strong></div>
            <div style="margin-top:6px;"><strong>Name,</strong></div>
            <div style="margin-top:6px;"><strong>Designation</strong></div>
            <div style="margin-top:6px;"><strong>Date and seal</strong></div>
          </div>
        </div>
      </div>
    `;
  };

  const columns = [
    {
      title: "Select",
      dataIndex: "id",
      key: "select",
      render: (id) => (
        <Checkbox
          onChange={(e) => handleOrderSelect(id, e.target.checked)}
          checked={selectedOrders.some((order) => order.id === id)}
        />
      ),
      width: 60,
    },
    { 
      title: "Order ID", 
      dataIndex: "id", 
      key: "id" 
    },
    { 
      title: "Customer", 
      dataIndex: ["customer", "customerName"], 
      key: "customerName",
      render: (text) => text || "N/A"
    },
    {
      title: "Date",
      dataIndex: "orderDate",
      key: "orderDate",
      render: (date) => (date ? dayjs(date).format("DD/MM/YYYY") : "N/A"),
    },
    {
      title: "Plant Size",
      dataIndex: "plantSizeKw",
      key: "plantSizeKw",
      render: (size) => (size ? `${size} KW` : "N/A"),
    },
    {
      title: "Installation Date",
      dataIndex: "installationCompletedDate",
      key: "installationCompletedDate",
      render: (date) => (date ? dayjs(date).format("DD/MM/YYYY") : "Not installed"),
    },
  ];

  return (
    <Modal
      title={
        <span>
          <FilePdfOutlined /> Generate Commissioning Report
        </span>
      }
      open={visible}
      onCancel={onCancel}
      width={1400}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Cancel
        </Button>,
        <Button
          key="generate"
          type="primary"
          icon={<DownloadOutlined />}
          onClick={generateProforma}
          loading={generating}
          disabled={selectedOrders.length === 0 || !previewHTML}
        >
          {generating ? "Generating..." : "Download Proforma (.docx)"}
        </Button>,
      ]}
    >
      <Row gutter={[24, 16]}>
        {/* Left Column - Order Selection */}
        <Col span={10}>
          <Card 
            title="Select Order" 
            size="small"
            extra={
              <span style={{ color: "#1890ff" }}>
                {selectedOrders.length} selected
              </span>
            }
          >
            <div style={{ marginBottom: 8, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Input.Search
                placeholder="Search by customer name, order ID..."
                allowClear
                onChange={(e) => setSearchText(e.target.value)}
                style={{ width: "100%" }}
                size="small"
              />
              {loadingAssets && (
                <span style={{ color: "#722ed1", marginLeft: 8, whiteSpace: "nowrap" }}>
                  Loading assets...
                </span>
              )}
            </div>
            <Table
              columns={columns}
              dataSource={(orders || []).filter((o) => {
                if (!searchText) return true;
                const s = searchText.toLowerCase();
                return (
                  String(o.id).includes(s) ||
                  (o.customer?.customerName || "").toLowerCase().includes(s) ||
                  (o.selectedConsumerNumber || "").toLowerCase().includes(s)
                );
              })}
              rowKey="id"
              size="small"
              pagination={{ pageSize: 5 }}
              scroll={{ x: true, y: 300 }}
            />
            
            {selectedOrders.length > 0 && (
              <div style={{ marginTop: 16, padding: 12, background: "#f6ffed", borderRadius: 4 }}>
                <Space direction="vertical" size="small" style={{ width: "100%" }}>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <strong>Order #{selectedOrders[0]?.id}</strong>
                    <span style={{ color: "#52c41a" }}>
                      {customerAssets.length} assets
                    </span>
                  </div>
                  <Divider style={{ margin: "8px 0" }} />
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span>Sanction Capacity:</span>
                    <strong>{form.getFieldValue("sanctionCapacity") || "3"} KW</strong>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span>PV Modules:</span>
                    <strong>
                      {form.getFieldValue("noOfPVModules") || form.getFieldValue("numberOfModules") || "Not Available"}
                    </strong>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span>Module Wattage:</span>
                    <strong>{form.getFieldValue("moduleWattage") || "540"} W</strong>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span>Order Items:</span>
                    <strong>{selectedOrders[0]?.items?.length || 0}</strong>
                  </div>
                </Space>
              </div>
            )}
          </Card>
        </Col>

        {/* Right Column - PDF Preview */}
        <Col span={14}>
          <Card 
            title={
              <span>
                <EyeOutlined /> PDF Preview
              </span>
            }
            size="small"
            extra={
              selectedOrders.length > 0 && previewHTML ? (
                <span style={{ color: "#52c41a" }}>Live Preview</span>
              ) : (
                <span style={{ color: "#999" }}>Select an order</span>
              )
            }
            style={{ 
              background: "#fafafa",
              height: "100%",
              minHeight: 500
            }}
            bodyStyle={{ 
              padding: 0,
              height: "100%",
              display: "flex",
              flexDirection: "column"
            }}
          >
            {previewHTML ? (
              <div 
                ref={previewRef}
                style={{
                  height: "600px",
                  overflowY: "auto",
                  backgroundColor: "white",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                  margin: "12px",
                  padding: "20px",
                  borderRadius: "4px"
                }}
              >
                <div dangerouslySetInnerHTML={{ __html: previewHTML }} />
              </div>
            ) : (
              <div style={{ 
                display: "flex", 
                flexDirection: "column", 
                alignItems: "center", 
                justifyContent: "center",
                height: "100%",
                minHeight: 500,
                color: "#999"
              }}>
                <EyeOutlined style={{ fontSize: 48, marginBottom: 16 }} />
                <p>Select an order to preview</p>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      <Form form={form} style={{ display: "none" }}>
        <Form.Item name="customerName" />
        <Form.Item name="consumerNo" />
        <Form.Item name="mobileNumber" />
        <Form.Item name="email" />
        <Form.Item name="address" />
        <Form.Item name="pincode" />
        <Form.Item name="district" />
        <Form.Item name="state" />
        <Form.Item name="reArrangementType" />
        <Form.Item name="reSource" />
        <Form.Item name="projectModel" />
        <Form.Item name="installedCapacityRooftopGround" />
        <Form.Item name="installedCapacityGround" />
        <Form.Item name="sanctionCapacity" />
        <Form.Item name="installedCapacity" />
        <Form.Item name="installationDate" />
        <Form.Item name="moduleMake" />
        <Form.Item name="moduleModel" />
        <Form.Item name="moduleWattage" />
        <Form.Item name="numberOfModules" />
        <Form.Item name="totalCapacity" />
        <Form.Item name="warrantyDetails" />
        <Form.Item name="inverterMaker" />
        <Form.Item name="inverterCapacity" />
        <Form.Item name="inverterRating" />
        <Form.Item name="mppt" />
        <Form.Item name="hpd" />
        <Form.Item name="manufacturingYear" />
        <Form.Item name="panelWarrantyYears" />
        <Form.Item name="inverterWarrantyYears" />
        <Form.Item name="plantWarrantyYears" />
        <Form.Item name="noOfPVModules" />
        <Form.Item name="moduleCapacity" />
      </Form>

      {/* Debug Panel */}
      {DEBUG_PROFORMA && selectedOrders.length > 0 && (
        <details style={{ marginTop: 8, fontSize: 11, color: "#666" }} open>
          <summary>🔍 Debug Information</summary>
          <div style={{ padding: 8, background: "#f5f5f5", borderRadius: 4 }}>
            <div><strong>Order ID:</strong> {selectedOrders[0]?.id}</div>
            <div><strong>Order Items Count:</strong> {selectedOrders[0]?.items?.length || 0}</div>
            <div><strong>Solar Panel Assets:</strong> {
              customerAssets.filter(a => 
                a.item?.itemType === "SolarPanel" || 
                a.item?.itemName?.toLowerCase().includes("panel")
              ).length
            }</div>
            <div><strong>Module Count (from items):</strong> {
              selectedOrders[0]?.items?.find(i => 
                i.item?.itemType === "SolarPanel" || 
                i.item?.itemName?.toLowerCase().includes("panel")
              )?.quantity || "Not found"
            }</div>
            <div><strong>Module Count (from form):</strong> {
              form.getFieldValue("noOfPVModules") || form.getFieldValue("numberOfModules") || "NOT FOUND"
            }</div>
            <div><strong>Module Wattage:</strong> {form.getFieldValue("moduleWattage") || "540"} W</div>
          </div>
        </details>
      )}
    </Modal>
  );
};

export default ProformaGenerator;
